import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from 'assets/icons/insights/close-icon.svg?react';
import { memo, useEffect, useState } from 'react';
import { useAnalytics } from 'services/analytics';
import { range, RATING_STARS_COUNT } from 'utils';

import StarDefaultIcon from '../../assets/star-default.svg?react';
import StarFilledIcon from '../../assets/star-filled.svg?react';
import { closeButtonSx, primaryButtonSx, starContainerSx, subtitleSx, titleSx } from './styles';

type TRatingStepProps = {
  onRate: (rating: number) => void;
  onClose: () => void;
  placement?: string;
  day?: number;
};

export const RatingStep = memo<TRatingStepProps>(({ onRate, onClose, placement, day }) => {
  const { trackEvent } = useAnalytics();
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [selectedStar, setSelectedStar] = useState<number | null>(null);

  const handleStarClick = (index: number): void => {
    setSelectedStar(index);
  };

  const handleRate = (): void => {
    if (selectedStar !== null) {
      trackEvent('app_feedback_rate_shared', {
        ...(placement && { placement }),
        ...(day && { day }),
        source: 'web',
        rating: selectedStar,
      });
      onRate(selectedStar);
    }
  };

  useEffect(() => {
    trackEvent('app_feedback_popup_shown', {
      ...(placement && { placement }),
      ...(day && { day }),
      source: 'web',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFilled = (index: number): boolean => (hoveredStar || selectedStar || 0) > index;

  return (
    <Stack sx={{ gap: 14, alignItems: 'center' }}>
      <IconButton sx={closeButtonSx} onClick={onClose}>
        <CloseIcon />
      </IconButton>

      <Stack sx={{ gap: 8, alignItems: 'center' }}>
        <Stack sx={{ gap: 3, alignItems: 'center' }}>
          <Typography sx={titleSx}>How&apos;s your experience so far?</Typography>
          <Typography sx={subtitleSx}>Your thoughts help us make LUMI better for you.</Typography>
        </Stack>

        <Stack sx={starContainerSx} onMouseLeave={() => setHoveredStar(null)}>
          {range({ length: RATING_STARS_COUNT }).map((i) => (
            <Box
              role="button"
              key={i}
              display="flex"
              alignItems="center"
              onClick={(): void => handleStarClick(i + 1)}
              onMouseEnter={(): void => setHoveredStar(i + 1)}
              sx={{ cursor: 'pointer' }}
            >
              {isFilled(i) ? <StarFilledIcon /> : <StarDefaultIcon />}
            </Box>
          ))}
        </Stack>
      </Stack>

      <Button sx={primaryButtonSx} disabled={selectedStar === null} onClick={handleRate}>
        Rate
      </Button>
    </Stack>
  );
});
